import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

const LAST_UPDATED = '2026-05-23';
const LAST_EVALUATED = '2026-05-23';

// Schema.org WebPage with the accessibility-specific properties that AI search bots
// (Perplexity, ChatGPT, Google AI Overviews) read when surfacing accessibility-related
// answers. Values track the conformance + evaluation claims in the page body — keep them
// in sync if the body content changes.
const ACCESSIBILITY_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Accessibility statement — EAA Proof',
  url: 'https://eaaproof.com/accessibility',
  description:
    "EAA Proof's own accessibility statement: WCAG 2.1 AA conformance, structured to the EU model accessibility statement template (Implementing Decision (EU) 2018/1523), generated by the same product we sell.",
  inLanguage: 'en',
  isAccessibleForFree: true,
  datePublished: LAST_UPDATED,
  dateModified: LAST_EVALUATED,
  accessibilityFeature: [
    'alternativeText',
    'structuralNavigation',
    'readingOrder',
    'displayTransformability',
    'highContrastDisplay',
    'ARIA',
    'tableOfContents',
  ],
  accessibilityHazard: 'none',
  accessibilityAPI: 'ARIA',
  accessibilityControl: ['fullKeyboardControl', 'fullMouseControl', 'fullTouchControl'],
  accessibilityStandard: 'WCAG 2.1 AA',
  isPartOf: {
    '@type': 'WebSite',
    name: 'EAA Proof',
    url: 'https://eaaproof.com',
  },
};

/**
 * Accessibility statement for eaaproof.com itself.
 *
 * Structured to match the EU Web Accessibility Directive (Directive 2016/2102)
 * model statement adopted by the European Commission, which is also the
 * structure the EAA (Directive 2019/882) inherits for digital services. The
 * same template is generated by our scanner for paying customers — we eat our
 * own dog food so this page is the canonical example of what "good" looks like.
 */
export default function AccessibilityPage() {
  useSeo({
    title: 'Accessibility statement',
    canonicalPath: '/accessibility',
    description:
      "EAA Proof's own accessibility statement: WCAG 2.1 AA conformance, structured to the EU model accessibility statement template (Implementing Decision (EU) 2018/1523), generated by the same product we sell.",
  });

  return (
    <div className="page legal-page" lang="en">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ACCESSIBILITY_SCHEMA) }}
      />
      <header>
        <h1>Accessibility statement</h1>
        <p className="muted">
          For <strong>eaaproof.com</strong>. Last updated: {LAST_UPDATED}.
        </p>
      </header>

      <section aria-labelledby="commitment">
        <h2 id="commitment">Our commitment</h2>
        <p>
          EAA Proof is committed to making this website accessible in line with
          the European Accessibility Act (Directive 2019/882), the Web
          Accessibility Directive (Directive 2016/2102), and the harmonised
          European standard <strong>EN 301 549 v3.2.1</strong>, which adopts{' '}
          <strong>WCAG 2.1 Level AA</strong> as its baseline for web content.
        </p>
        <p>
          We sell accessibility scans, so it would be embarrassing if our own
          site failed the rules we audit other sites against. This page
          describes the conformance status of <strong>eaaproof.com</strong>,
          what we test, and how to report a problem.
        </p>
      </section>

      <section aria-labelledby="conformance">
        <h2 id="conformance">Conformance status</h2>
        <p>
          This website is <strong>fully conformant</strong> with WCAG 2.1 Level
          AA. "Fully conformant" means the content complies with every
          applicable success criterion without exception.
        </p>
        <p>
          We also voluntarily meet several Level AAA criteria where they
          improve the experience without forcing a content trade-off — most
          notably 2.5.5 Target Size (≥44×44 px for primary actions), 1.4.6
          Contrast (Enhanced) on body text, and 2.3.3 Animation from
          Interactions (we honour <code>prefers-reduced-motion</code>).
        </p>
      </section>

      <section aria-labelledby="non-accessible">
        <h2 id="non-accessible">Non-accessible content</h2>
        <p>
          We are not aware of any content on this website that fails WCAG 2.1
          Level AA at the time of writing. If you discover something that does
          not meet the standard, please tell us — see{' '}
          <a href="#feedback">Feedback</a> below.
        </p>
        <p>
          The following content is <strong>out of scope</strong> of the
          European Accessibility Act and is therefore not covered by this
          statement:
        </p>
        <ul>
          <li>
            PDF reports and HTML accessibility statements <em>generated for
            customers</em> describe the customer's site, not ours. Their
            accessibility is the responsibility of the customer who publishes
            them.
          </li>
          <li>
            Third-party content loaded inside iframes during a scan (for
            example, the target site being scanned).
          </li>
        </ul>
      </section>

      <section aria-labelledby="preparation">
        <h2 id="preparation">Preparation of this statement</h2>
        <p>
          This statement was prepared on <strong>{LAST_UPDATED}</strong> and
          last reviewed on <strong>{LAST_EVALUATED}</strong>. It is reviewed
          whenever the site undergoes substantial UI changes, and at minimum
          annually.
        </p>
        <h3>Evaluation method</h3>
        <p>
          The site was evaluated using a combination of:
        </p>
        <ul>
          <li>
            <strong>Automated testing</strong> with axe-core 4.x (the same
            engine that powers the scans we sell), run against every page in
            CI.
          </li>
          <li>
            <strong>Manual keyboard testing</strong> of the full user journey
            (sign-up → scan → download report → manage subscription) using only
            <kbd>Tab</kbd>, <kbd>Shift+Tab</kbd>, <kbd>Enter</kbd>, and{' '}
            <kbd>Space</kbd>.
          </li>
          <li>
            <strong>Screen-reader testing</strong> with NVDA (Firefox), VoiceOver
            (Safari, macOS &amp; iOS), and TalkBack (Chrome, Android).
          </li>
          <li>
            <strong>200% zoom</strong> and <strong>400% browser zoom</strong>{' '}
            reflow checks (WCAG 1.4.10).
          </li>
          <li>
            <strong>Reduced-motion</strong> verification with the OS-level
            "Reduce motion" setting enabled.
          </li>
        </ul>
      </section>

      <section aria-labelledby="compatibility">
        <h2 id="compatibility">Compatibility</h2>
        <p>This site is designed to work with:</p>
        <ul>
          <li>Recent versions of Chrome, Edge, Firefox, and Safari on desktop and mobile.</li>
          <li>Screen readers: NVDA, JAWS, VoiceOver, TalkBack.</li>
          <li>Browser zoom up to 400% and OS-level text scaling up to 200%.</li>
          <li>Keyboard-only navigation throughout, with a visible focus indicator on every interactive element.</li>
          <li>OS-level "reduce motion" preferences — non-essential animation is suppressed automatically.</li>
        </ul>
        <p>
          This site is not designed to work with Internet Explorer or browsers
          without JavaScript enabled. A no-JavaScript fallback message points
          users to <a href="mailto:support@eaaproof.com">support@eaaproof.com</a>{' '}
          for a manual scan request.
        </p>
      </section>

      <section aria-labelledby="technical">
        <h2 id="technical">Technical specifications</h2>
        <p>
          Accessibility of this site relies on the following technologies,
          which must be supported in the user agent:
        </p>
        <ul>
          <li>HTML 5 with WAI-ARIA where native semantics are not sufficient</li>
          <li>CSS 3 (cascade, custom properties, <code>prefers-reduced-motion</code>, <code>prefers-color-scheme</code>)</li>
          <li>JavaScript (ECMAScript 2022)</li>
        </ul>
      </section>

      <section aria-labelledby="feedback" id="feedback">
        <h2 id="feedback-heading">Feedback</h2>
        <p>
          We welcome feedback on the accessibility of this website. If you find
          an accessibility barrier, please contact us so we can fix it:
        </p>
        <ul>
          <li>
            Email:{' '}
            <a href="mailto:support@eaaproof.com?subject=Accessibility%20feedback">
              support@eaaproof.com
            </a>
          </li>
        </ul>
        <p>
          We aim to respond to accessibility feedback within{' '}
          <strong>5 working days</strong> and to provide a remediation or
          accessible alternative within <strong>30 days</strong>.
        </p>
      </section>

      <section aria-labelledby="enforcement">
        <h2 id="enforcement">Enforcement procedure</h2>
        <p>
          If we fail to respond satisfactorily to your accessibility feedback,
          you have the right to escalate to the national enforcement body in
          your EU Member State. Each Member State has designated one or more
          competent authorities under Article 14 of the European Accessibility
          Act. A list is maintained by the European Commission at{' '}
          <a
            href="https://ec.europa.eu/social/main.jsp?catId=1202"
            target="_blank"
            rel="noopener noreferrer"
          >
            ec.europa.eu/social/main.jsp?catId=1202
          </a>
          .
        </p>
      </section>

      <section aria-labelledby="template">
        <h2 id="template">Use this as a template</h2>
        <p>
          This page is itself the template our scanner generates for paying
          customers. You're welcome to adapt the structure for your own site —
          but you must replace the conformance claim with one that reflects
          your <em>actual</em> testing. A false conformance claim is itself a
          violation under Article 13(2) of Directive 2019/882.
        </p>
        <p>
          To audit your own site and generate a statement with the correct
          conformance claim baked in, <Link to="/">run a scan</Link> or{' '}
          <Link to="/pricing">see pricing</Link>.
        </p>
      </section>
    </div>
  );
}
